import DialpadIcon from "@mui/icons-material/Dialpad";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import type { BottomApplicationLink } from "Components/Layout/Menu/BottomMenuItem";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import {
  CoinsIcon,
  FriendsIcon,
  HomeIcon,
  InstagramVerificationBadgeIcon,
  MerchantAccountIcon,
  MoreIcon,
  NotificationIcon,
  PlusMathIcon,
  SelectUsersIcon,
  SortingArrowsIcon,
} from "Components/Shared/Icons";
import {
  type AccessRightCode,
  AccessRightCodes,
} from "Models/AccessRightCodes";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const ICON_SIZE = 16;
const RESOURCES = Resources.Menu.BottomMenuItems;

export type BottomApplicationLinkWithAccessCode = BottomApplicationLink & {
  accessRightCode: AccessRightCode;
};

export const useBottomMenuLinks = () => {
  const { t } = useResource();

  const links = {
    overview: {
      label: t(RESOURCES.Overview),
      value: appUrl("dashboard"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <HomeIcon height={ICON_SIZE} />,
    },
    transactions: {
      label: t(RESOURCES.History),
      value: appUrl("transactions"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <SortingArrowsIcon height={ICON_SIZE} />,
    },
    merchantKeypadPayment: {
      label: t(RESOURCES.InitiateKeypadPayment),
      value: appUrl("initiate-keypad-payment"),
      accessRightCode: AccessRightCodes.Dashboard,
      isAction: true,
      icon: <DialpadIcon sx={{ height: ICON_SIZE }} />,
    },
    employeeKeypadPayment: {
      label: t(RESOURCES.InitiateKeypadPayment),
      value: appUrl("employee-initiate-keypad-payment"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <DialpadIcon sx={{ height: ICON_SIZE }} />,
    },
    merchants: {
      label: t(RESOURCES.Merchants),
      value: appUrl("merchants"),
      accessRightCode: AccessRightCodes.Dashboard, //TODO: maybe new AccessRightCode.Merchants?
      icon: <CoinsIcon height={ICON_SIZE} />,
    },
    licenses: {
      label: t(RESOURCES.Licenses),
      value: appUrl("licenses"),
      accessRightCode: AccessRightCodes.Dashboard, //TODO: maybe new AccessRightCode.License?
      icon: <InstagramVerificationBadgeIcon height={ICON_SIZE} />,
    },
    createMerchantRegistration: {
      label: t(RESOURCES.NewMerchant),
      value: appUrl("merchant-new-registration"),
      isAction: true,
      accessRightCode: AccessRightCodes.Dashboard, //TODO: maybe new AccessRightCode.License?
      icon: <PlusMathIcon height={ICON_SIZE} />,
    },
    bankAccounts: {
      label: t(RESOURCES.Accounts),
      value: appUrl("bank-address-list"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MerchantAccountIcon height={ICON_SIZE} />,
    },
    settings: {
      label: t(RESOURCES.Settings),
      value: appUrl("settings"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MoreIcon height={ICON_SIZE} />,
    },
    users: {
      label: t(RESOURCES.Users),
      value: appUrl("user-list"),
      accessRightCode: AccessRightCodes.Users,
      icon: <SelectUsersIcon height={ICON_SIZE} />,
    },
    amlUsers: {
      label: t(RESOURCES.Users),
      value: appUrl("aml-users"),
      accessRightCode: AccessRightCodes.Users,
      icon: <SelectUsersIcon height={ICON_SIZE} />,
    },
    stardust: {
      label: t(RESOURCES.Stardust),
      value: appUrl("stardust"),
      isAction: true,
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <AutoAwesomeIcon sx={{ height: ICON_SIZE }} />,
    },
    valor: {
      label: t(RESOURCES.Valor),
      value: appUrl("valor"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MilitaryTechIcon sx={{ height: ICON_SIZE + 4 }} />,
    },
    merchantSettlements: {
      label: t(RESOURCES.Settlement),
      value: appUrl("merchant-settlements"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <MerchantAccountIcon height={ICON_SIZE} />,
    },
    thanksToYou: {
      label: t(RESOURCES.ThanksToYou),
      value: appUrl("affiliate"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <FriendsIcon height={ICON_SIZE} />,
    },
    notificationsManagement: {
      label: t(RESOURCES.NotificationsManagement),
      value: appUrl("notifications-management"),
      accessRightCode: AccessRightCodes.Users,
      icon: <NotificationIcon height={ICON_SIZE} />,
    },
    toBeVerified: {
      label: t(RESOURCES.ToBeVerified),
      value: appUrl("to-be-verified"),
      accessRightCode: AccessRightCodes.Users,
      icon: <FactCheckIcon sx={{ height: ICON_SIZE }} />,
    },
    hunters: {
      label: t(RESOURCES.Hunters),
      value: appUrl("hunters"),
      accessRightCode: AccessRightCodes.Hunters,
      icon: <SelectUsersIcon height={ICON_SIZE} />,
    },
    whereToPay: {
      label: t(RESOURCES.WhereToPay),
      value: appUrl("where-to-pay"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <StorefrontIcon sx={{ height: ICON_SIZE }} />,
    },
    chat: {
      label: t(RESOURCES.Chat),
      value: appUrl("chat"),
      accessRightCode: AccessRightCodes.Dashboard,
      icon: <ChatBubbleOutlineIcon sx={{ height: ICON_SIZE }} />,
    },
  } satisfies Record<string, BottomApplicationLinkWithAccessCode>;

  return links;
};
